import { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import Button, { ButtonType } from '../Button/Button';
import Input from '../Input/Input';
import styles from './LoginForm.module.scss';
import Divider from '../Divider/Divider';
import Checkbox from '../Checkbox/Checkbox';

export type LoginFormValues = { email: string; password: string; remember: boolean };
export type LoginFormProps = {
  onSubmit?: (values: LoginFormValues) => void;
  forgotPasswordHref?: string;
  startJourneyHref?: string;
  loading?: boolean;
  errorMessage?: string | undefined;
};

export function LoginForm({
  onSubmit,
  forgotPasswordHref = '#',
  startJourneyHref = '#',
  loading = false,
  errorMessage = undefined,
}: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const t = useTranslations('Login');

  const canSubmit = useMemo(
    () => !loading && email.trim().length > 0 && password.trim().length > 0,
    [loading, email, password],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit?.({ email, password, remember });
  };

  return (
    <div
      className={styles.form}
    >
      <form
        aria-label='Login form'
        onSubmit={handleSubmit}
      >
        <div className={styles.inputGroup}>
          <Input
            id='email'
            name='email'
            type='email'
            label={t('Email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete='email'
            className={styles.loginInput}
            required
          />

          <Input
            id='password'
            name='password'
            type='password'
            label={t('Password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete='current-password'
            className={styles.loginInput}
            errorText={errorMessage}
            required
          />
        </div>

        <div className={styles.rowBetween}>
          <Checkbox
            id='remember'
            name='remember'
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            label={t('Remember me')}
          />
          <a
            href={forgotPasswordHref}
            className={styles.link}
          >
            {t('Forgot your password?')}
          </a>
        </div>

        <div className={styles.loginButtonWrap}>
          <Button
            type={ButtonType.PRIMARY_LIGHT}
            btnType='submit'
            loading={loading}
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
          >
            {t('Login')}
          </Button>
        </div>

        <Divider />

        <p className={styles.footerText}>
          {t('Don’t have an account?')}
          {' '}
          <a
            href={startJourneyHref}
            className={styles.startJourneyLink}
          >
            {t('Start your journey')}
          </a>
        </p>
      </form>
    </div>
  );
}
